import React, { useEffect, useMemo, useState } from 'react';
import { fetchAvailablePdfTemplates } from '../utils/pdfTemplates';
import { submitContactRequest } from '../utils/contact';

const TEXT = {
    title: 'צור קשר',
    description: 'אפשר לדווח על תקלה, לבקש פיתוח, לבקש שינוי בתבנית או לשלוח פנייה כללית.',
    messageType: 'סוג פנייה',
    message: 'פירוט',
    templates: 'תבניות קשורות',
    templatesOpen: 'פתח בחירת תבניות',
    templatesClose: 'סגור בחירת תבניות',
    templatesEmpty: 'אין תבניות זמינות.',
    selectedCount: 'נבחרו',
    jobExamples: 'דוגמאות לעבודות / הערות נוספות',
    send: 'שלח',
    sending: 'שולח...',
    success: 'הפנייה נשלחה בהצלחה.',
    loadError: 'לא ניתן לטעון את רשימת התבניות כרגע.',
    sendError: 'לא ניתן לשלוח את הפנייה כרגע.',
    placeholder: 'כתבו כאן את כל הפרטים שחשובים לכם, כולל דוגמאות והתנהגות רצויה.',
};

const MESSAGE_TYPES = [
    { value: 'bug', label: 'דיווח על תקלה' },
    { value: 'feature_request', label: 'בקשת פיתוח' },
    { value: 'template_change', label: 'שינוי בתבנית' },
    { value: 'general', label: 'פנייה כללית' },
];

function buildTemplateSummary(selectedCount) {
    if (selectedCount > 0) {
        return `${TEXT.templates} (${TEXT.selectedCount}: ${selectedCount})`;
    }
    return TEXT.templates;
}

export default function ContactUs() {
    const [templates, setTemplates] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
    const [formState, setFormState] = useState({
        messageType: 'bug',
        templateIds: [],
        jobExamples: '',
        message: '',
    });

    useEffect(() => {
        let isMounted = true;
        fetchAvailablePdfTemplates()
            .then((data) => {
                if (!isMounted) return;
                setTemplates(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                if (!isMounted) return;
                setLoadError(TEXT.loadError);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    const selectedTemplateIds = useMemo(
        () => new Set(formState.templateIds.map((value) => String(value))),
        [formState.templateIds],
    );

    const toggleTemplate = (templateId) => {
        setFormState((prev) => {
            const nextIds = new Set(prev.templateIds.map((value) => String(value)));
            const key = String(templateId);
            if (nextIds.has(key)) {
                nextIds.delete(key);
            } else {
                nextIds.add(key);
            }
            return {
                ...prev,
                templateIds: Array.from(nextIds),
            };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            setIsSubmitting(true);
            setSubmitError('');
            setSubmitSuccess('');
            await submitContactRequest({
                messageType: formState.messageType,
                templateIds: formState.templateIds,
                jobExamples: formState.jobExamples.trim(),
                message: formState.message.trim(),
            });
            setSubmitSuccess(TEXT.success);
            setFormState((prev) => ({
                ...prev,
                jobExamples: '',
                message: '',
                templateIds: [],
            }));
            setIsTemplatesOpen(false);
        } catch (err) {
            setSubmitError(err.response?.data?.message || TEXT.sendError);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="page page--accent stack stack--lg">
            <header className="page__header">
                <div className="stack">
                    <h1 className="page__title">{TEXT.title}</h1>
                    <p className="page__description">{TEXT.description}</p>
                </div>
            </header>

            <form className="card stack" onSubmit={handleSubmit}>
                <label className="form-field">
                    <span className="form-field__label">{TEXT.messageType}</span>
                    <select
                        className="form-field__control"
                        value={formState.messageType}
                        onChange={(event) =>
                            setFormState((prev) => ({ ...prev, messageType: event.target.value }))
                        }
                    >
                        {MESSAGE_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="form-field">
                    <span className="form-field__label">{TEXT.message}</span>
                    <textarea
                        className="form-field__control form-field__control--textarea"
                        rows={8}
                        value={formState.message}
                        onChange={(event) =>
                            setFormState((prev) => ({ ...prev, message: event.target.value }))
                        }
                        placeholder={TEXT.placeholder}
                        required
                    />
                </label>

                <label className="form-field">
                    <span className="form-field__label">{TEXT.jobExamples}</span>
                    <textarea
                        className="form-field__control form-field__control--textarea"
                        rows={3}
                        value={formState.jobExamples}
                        onChange={(event) =>
                            setFormState((prev) => ({ ...prev, jobExamples: event.target.value }))
                        }
                    />
                </label>

                <details
                    className="contact-us__templates"
                    open={isTemplatesOpen}
                    onToggle={(event) => setIsTemplatesOpen(event.currentTarget.open)}
                >
                    <summary className="contact-us__templates-summary">
                        <span>{buildTemplateSummary(formState.templateIds.length)}</span>
                        <span className="card__meta">
                            {isTemplatesOpen ? TEXT.templatesClose : TEXT.templatesOpen}
                        </span>
                    </summary>
                    <div className="contact-us__templates-body">
                        {loadError ? (
                            <div className="alert alert--danger">{loadError}</div>
                        ) : templates.length === 0 ? (
                            <div className="card__meta">{TEXT.templatesEmpty}</div>
                        ) : (
                            <div className="chip-list">
                                {templates.map((template) => {
                                    const isSelected = selectedTemplateIds.has(String(template.id));
                                    return (
                                        <button
                                            key={template.id}
                                            type="button"
                                            className={`chip chip--selectable${isSelected ? ' chip--selected' : ''}`}
                                            onClick={() => toggleTemplate(template.id)}
                                        >
                                            {template.name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </details>

                {submitError && <div className="alert alert--danger">{submitError}</div>}
                {submitSuccess && <div className="alert alert--success">{submitSuccess}</div>}

                <div className="flex">
                    <button type="submit" className="button button--primary" disabled={isSubmitting}>
                        {isSubmitting ? TEXT.sending : TEXT.send}
                    </button>
                </div>
            </form>
        </div>
    );
}
